import { useState } from 'react'
import { discussions } from '@/mocks/home'
import SectionHeading from './SectionHeading'

export default function DiscussionSection() {
  const [draft, setDraft] = useState('')
  const [hint, setHint] = useState(false)
  const [liked, setLiked] = useState<Record<string, boolean>>({})

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleCompose = () => {
    setHint(true)
  }

  return (
    <section id="discussion" className="border-b border-background-200/80 bg-background-100/60">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Community"
          title="群众讨论 · 边看边聊"
          desc="围观不该只有一个人。这里放着大家正在聊的片段，理性发言，等后续出来再一起下结论。"
          actionLabel="查看讨论规范"
          actionHref="#subscribe"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px] lg:gap-12">
          <div>
            <div className="mb-6 rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                  我
                </span>
                <div className="flex-1">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, 500))}
                    maxLength={500}
                    rows={2}
                    placeholder="说说你在蹲什么，理性围观，不传未经核实的说法……"
                    className="w-full resize-none rounded-md border border-background-300/80 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs text-foreground-500">最多 500 字 · {draft.length}/500</span>
                    <button
                      type="button"
                      onClick={handleCompose}
                      className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                    >
                      发表看法
                    </button>
                  </div>
                  {hint && (
                    <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800">
                      <i className="ri-information-line text-sm"></i>
                      这是演示页面，内容暂不对外提交，谢谢你愿意先聊两句。
                    </p>
                  )}
                </div>
              </div>
            </div>

            <ul className="divide-y divide-background-200/80 border-t border-background-200/80">
              {discussions.map((item) => (
                <li key={item.id} className="flex gap-3 py-5 md:gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-bold text-accent-800">
                    {item.initial}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground-900">{item.user}</span>
                      <span className="rounded-md bg-background-200/80 px-1.5 py-0.5 text-[11px] text-foreground-600">
                        {item.topic}
                      </span>
                      <span className="text-xs text-foreground-500">{item.time}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                    <div className="mt-2 flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => toggleLike(item.id)}
                        className={`inline-flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${
                          liked[item.id] ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                        }`}
                      >
                        <i className={liked[item.id] ? 'ri-heart-3-fill text-sm' : 'ri-heart-3-line text-sm'}></i>
                        {item.likes + (liked[item.id] ? 1 : 0)}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-800 cursor-pointer"
                      >
                        <i className="ri-chat-3-line text-sm"></i>
                        回复
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-shield-check-line text-lg text-accent-600"></i>
                围观守则
              </h3>
              <ul className="space-y-3 text-sm leading-relaxed text-foreground-700">
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                  未见定论的，先按"待后续"处理，别急着站队。
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                  截图请尽量带上原始时间，方便大家一起对时间线。
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary-500"></span>
                  讨论对人不对事少一些，聊事实多一些。
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h3 className="mb-3 font-heading text-base font-bold text-foreground-950">今晚的蹲守提示</h3>
              <p className="text-sm leading-relaxed text-foreground-600">
                有 3 条话题被标记为"可能更新"，建议在睡前再刷一次合集索引，别错过后续。
              </p>
              <a
                href="#collections"
                className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800 cursor-pointer"
              >
                去看待更新合集
                <i className="ri-arrow-right-line text-base"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  )
}